import { z } from 'zod';
import {
  ACTION_TYPES,
  GRANTABLE_CAPABILITIES,
} from './capabilities.js';
import {
  acceptedAuthTypes,
  credentialGuidance,
  AUTH_TYPE,
  GENERATION_KIND,
  GENERATION_MODE,
  OVERAGE_BEARER,
  PERIOD_UNIT,
  PLATFORM,
  TRANSPORT,
  BOT_VISIBILITY,
  DM_REPLY_MODE,
  authTypeMatchesTransport,
  supportsTransport,
} from './enums.js';

const uuid = z.string().uuid();
const minorUnits = z.number().int().nonnegative();

export const signupSchema = z.object({
  email: z.string().email().max(320),
  password: z.string().min(12).max(200),
  handle: z
    .string()
    .min(3)
    .max(30)
    .regex(/^[a-z0-9_]+$/, 'lowercase letters, digits and underscores only'),
  country: z.string().length(2).toUpperCase(),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  totp: z.string().regex(/^\d{6}$/).optional(),
});

export const createBotSchema = z.object({
  name: z.string().min(2).max(80),
  slug: z
    .string()
    .min(3)
    .max(60)
    .regex(/^[a-z0-9-]+$/, 'lowercase letters, digits and hyphens only'),
});

export const updateBotSchema = z
  .object({
    name: z.string().min(2).max(80),
    visibility: z.enum(BOT_VISIBILITY),
    avatar_asset_id: uuid.nullable(),
    /** §7.3 bot control. See `DM_REPLY_MODE` for why this is not a boolean. */
    dm_reply_mode: z.enum(DM_REPLY_MODE),
  })
  .partial();

/**
 * CharacterProfile. `nsfw_policy` is absent by design — NSFW is off platform-wide
 * and there is no field to configure (§0.5, §12.3). Unknown keys are rejected so
 * that a client sending `nsfw_policy` gets a 400 rather than silent acceptance.
 */
export const characterSchema = z
  .object({
    description: z.string().min(1).max(20_000),
    tone: z.string().max(2_000).default(''),
    topics_allow: z.array(z.string().min(1).max(80)).max(50).default([]),
    topics_deny: z.array(z.string().min(1).max(80)).max(200).default([]),
    style_examples: z.array(z.string().max(4_000)).max(20).default([]),
    language: z.array(z.string().min(2).max(10)).min(1).default(['en']),
    locked_fields: z.array(z.string().max(40)).max(20).default([]),
  })
  .strict();

export const attachSocialAccountSchema = z
  .object({
    platform: z.enum(PLATFORM),
    /**
     * The owner's choice: reach this account over the platform's official API
     * with their own developer credentials, or drive a logged-in session in a
     * headless browser. Defaults to `api` — the cheaper, lower-risk path is what
     * you get by not choosing.
     */
    transport: z.enum(TRANSPORT).default('api'),
    auth_type: z.enum(AUTH_TYPE),
    external_handle: z.string().min(1).max(200),
    /**
     * Write-only secret. Never echoed back by any endpoint (§3.2 note 1).
     * For Bluesky this must be an app password, never the account password.
     * For the browser transport this is the serialized session blob captured
     * from a login the owner performed themselves — never a password, never a
     * TOTP seed (§11.2).
     */
    secret: z.string().min(1).max(200_000),
    /**
     * Supplied alongside an OAuth 2 access token so the platform can renew it.
     * Without these the token simply expires and the owner reconnects by hand.
     */
    refresh_token: z.string().max(4_000).optional(),
    expires_in: z.number().int().positive().max(31_536_000).optional(),
    /** OAuth flows supply a refresh token; app-password flows supply the app password. */
    scopes: z.array(z.string()).max(50).default([]),
    acknowledged_disclosure: z.literal(true),
  })
  .strict()
  .refine((v) => authTypeMatchesTransport(v.auth_type, v.transport), {
    message:
      'the browser transport requires auth_type "session"; the api transport requires anything but',
    path: ['auth_type'],
  })
  .refine((v) => supportsTransport(v.platform, v.transport), {
    message: 'that platform has no adapter for that transport',
    path: ['transport'],
  })
  /**
   * The two refinements above check the transport in isolation: browser needs a
   * session, and the platform must have an adapter. Neither is platform-aware,
   * which is how X accepted `app_password` — a Bluesky concept X has never had —
   * and the value the owner actually typed was their account password.
   *
   * §11.2 forbids storing one. That promise only holds if the shape is rejected
   * before the secret reaches the vault, so this is the check that makes the
   * documented rule true.
   */
  .superRefine((v, ctx) => {
    const accepted = acceptedAuthTypes(v.platform, v.transport);
    if (accepted.includes(v.auth_type)) return;
    ctx.addIssue({
      code: 'custom',
      path: ['auth_type'],
      message:
        `${v.platform} over ${v.transport} accepts ` +
        `${accepted.join(' or ') || 'nothing'}, not ${v.auth_type}. ` +
        credentialGuidance(v.platform, v.transport),
    });
  });

export const createListingSchema = z
  .object({
    rental_price: minorUnits,
    currency: z.string().length(3).toUpperCase(),
    period_unit: z.enum(PERIOD_UNIT),
    period_count: z.number().int().min(1).max(365),
    min_periods: z.number().int().min(1).max(365),
    max_periods: z.number().int().min(1).max(365),
    granted_permissions: z.array(z.enum(GRANTABLE_CAPABILITIES)).max(20).default([]),
    included_generation_credits: z
      .object({ text: z.number().int().min(0).max(100_000), image: z.number().int().min(0).max(100_000) })
      .default({ text: 0, image: 0 }),
    generation_overage_bearer: z.enum(OVERAGE_BEARER).default('renter'),
    terms: z.string().max(10_000).default(''),
  })
  .strict()
  .refine((v) => v.min_periods <= v.max_periods, {
    message: 'min_periods must be <= max_periods',
    path: ['min_periods'],
  });

export const createRentalSchema = z
  .object({
    listing_id: uuid,
    period_count: z.number().int().min(1).max(365),
    start_at: z.string().datetime({ offset: true }),
    /** FR-4.2: the disclosure is actively acknowledged, never pre-checked. */
    disclosure_acknowledged: z.literal(true),
  })
  .strict();

export const generationRequestSchema = z
  .object({
    kind: z.enum(GENERATION_KIND),
    mode: z.enum(GENERATION_MODE),
    prompt: z.string().min(1).max(8_000),
    count: z.number().int().min(1).max(20).default(1),
  })
  .strict();

/**
 * Action creation.
 *
 * `dm` is now a member of `ACTION_TYPES` (migration 004 reversed §0.4), so
 * `{"type":"dm"}` passes schema validation and is decided by the authorization
 * layer instead. AC-9 changes with it: an ungranted DM is a **403**, where it
 * used to be a 400 for an unknown word. The guarantee is weaker on purpose, and
 * `DM_CONSTRAINTS` carries what the absence used to.
 */
export const createActionSchema = z
  .object({
    type: z.enum(ACTION_TYPES),
    social_account_id: uuid,
    body: z.string().max(10_000).default(''),
    asset_ids: z.array(uuid).max(4).default([]),
    target_ref: z.record(z.string(), z.unknown()).nullable().default(null),
    scheduled_at: z.string().datetime({ offset: true }).nullable().default(null),
  })
  .strict();

export const reviewSchema = z
  .object({
    rating: z.number().int().min(1).max(5),
    body: z.string().max(4_000).default(''),
  })
  .strict();

export const feeScheduleSchema = z
  .object({
    effective_from: z.string().datetime({ offset: true }),
    currency: z.string().length(3).toUpperCase().default('USD'),
    bot_creation_fee: minorUnits,
    /** §8.2 guardrail: a listing fee of zero decouples revenue from live inventory. */
    listing_fee_amount: z.number().int().positive(),
    listing_fee_interval: z.literal('month'),
    generation_fee_text: minorUnits,
    generation_fee_image: minorUnits,
    generation_fee_mode: z.enum(['flat', 'cost_plus']).default('flat'),
    generation_fee_markup_percent: z.number().min(0).max(1000).default(0),
    generation_fee_minimum: minorUnits.default(0),
    credit_pack_definitions: z
      .array(
        z.object({
          sku: z.string().min(1).max(60),
          label: z.string().min(1).max(120),
          price: minorUnits,
          text_credits: z.number().int().min(0),
          image_credits: z.number().int().min(0),
        }),
      )
      .default([]),
    credit_expiry_days: z.number().int().min(1).max(3650).default(365),
    refund_window_hours: z.number().int().min(0).max(720).default(24),
  })
  .strict();

export const consentResponseSchema = z.object({ accept: z.boolean() }).strict();

export const paginationSchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/**
 * Activity log query (§7.2).
 *
 * `window` is a named range rather than a from/to pair. §7.6 requires the log to
 * state the period it covers, and a named window is the only form where the
 * server's echo cannot disagree with what the reader picked — arbitrary bounds
 * would let the two drift apart on clock skew or timezone.
 */
export const activityQuerySchema = paginationSchema.extend({
  window: z.enum(['24h', '7d', '30d', 'all']).default('7d'),
  rental_id: z.string().uuid().optional(),
});

export const listingSearchSchema = paginationSchema.extend({
  q: z.string().max(200).optional(),
  platform: z.enum(PLATFORM).optional(),
  min_price: z.coerce.number().int().min(0).optional(),
  max_price: z.coerce.number().int().min(0).optional(),
  period_unit: z.enum(PERIOD_UNIT).optional(),
  topic: z.string().max(80).optional(),
  language: z.string().max(10).optional(),
  min_rating: z.coerce.number().min(0).max(5).optional(),
  min_health: z.coerce.number().min(0).max(1).optional(),
  available_from: z.string().datetime({ offset: true }).optional(),
  available_to: z.string().datetime({ offset: true }).optional(),
  permission: z.enum(GRANTABLE_CAPABILITIES).optional(),
  sort: z
    .enum(['relevance', 'price_asc', 'price_desc', 'rating', 'health', 'recent', 'most_rented'])
    .default('relevance'),
});

/**
 * Auto-reply listening rule. `query` is passed straight to the adapter's
 * `search(q)` — a hashtag, a keyword, or anything the platform's search accepts.
 * `confidence_threshold` and `daily_reply_cap` both have safe defaults so a rule
 * created without touching either still bounds its own worst case.
 */
export const listeningReplyModeSchema = z.enum(['auto', 'approve']);

export const createListeningRuleSchema = z
  .object({
    social_account_id: uuid,
    query: z.string().min(1).max(200),
    confidence_threshold: z.number().min(0).max(1).default(0.8),
    daily_reply_cap: z.number().int().min(0).max(500).default(20),
    enabled: z.boolean().default(true),
    // Extra scoring instructions beyond persona voice/topic fit, e.g. intent
    // filtering ("only reply to buyers, not sellers"). Folded into the same
    // scoring prompt rather than a second model call.
    reply_criteria: z.string().max(500).optional(),
    // 'approve' is the default deliberately: a new rule should not send on its
    // own until the owner has seen what it drafts.
    reply_mode: listeningReplyModeSchema.default('approve'),
    // Overrides the platform's default text model for this rule's
    // scoring/reply call. Checked against known model ids at the route layer,
    // not here — the set of valid ids lives with the generation provider.
    model: z.string().min(1).max(100).optional(),
  })
  .strict();

export const updateListeningRuleSchema = z
  .object({
    query: z.string().min(1).max(200).optional(),
    confidence_threshold: z.number().min(0).max(1).optional(),
    daily_reply_cap: z.number().int().min(0).max(500).optional(),
    enabled: z.boolean().optional(),
    reply_criteria: z.string().max(500).optional(),
    reply_mode: listeningReplyModeSchema.optional(),
    model: z.string().min(1).max(100).optional(),
  })
  .strict();

/** Manually sending a `needs_review` match; an empty body keeps the model's draft. */
export const sendListeningMatchSchema = z
  .object({
    body: z.string().min(1).max(10_000).optional(),
  })
  .strict();
